import React, { useState, useEffect } from "react";
import { Send, Loader2 } from "lucide-react";
import CountryCodeSelect, { countryCodes } from "./CountryCodeSelect";
import { useGeo } from "../context/GeoContext";
import { usePricing } from "../context/PricingContext";
import { submitLead } from "../services/api";

const ContactForm = ({
  title = "Get Started Today",
  subtitle = "",
  packageTitle: packageTitleProp = "",
  packageAmount: packageAmountProp = "",
}) => {
  const geoData = useGeo();
  const pricing = usePricing();
  const packageTitle =
    packageTitleProp || pricing?.packageTitle || "Basic Package";
  const packageAmount = packageAmountProp || pricing?.packageAmount || "$45";
  const tmSignUpType = pricing?.tmSignUpType || 1;
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    countryCode: "+1",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const match = countryCodes.find(
        (c) => c.label === geoData.data.countryCode,
      );
      if (match) {
        setFormData((prev) => ({ ...prev, countryCode: match.code }));
      }
    }
  }, [geoData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email))
        newErrors.email =
          "Please enter a valid email address (e.g., user@example.com)";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else {
      const len = formData.phone.replace(/[^0-9]/g, "").length;
      if (len < 7 || len > 15)
        newErrors.phone = "Please enter a valid phone number (7-15 digits)";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const digitsOnly = value.replace(/[^0-9]/g, "");
      if (digitsOnly.length <= 15) {
        setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      }
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (apiError) setApiError("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "email" && value.trim()) {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value))
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address (e.g., user@example.com)",
        }));
    }
    if (name === "phone" && value.trim()) {
      const len = value.replace(/[^0-9]/g, "").length;
      if (len < 7 || len > 15)
        setErrors((prev) => ({
          ...prev,
          phone: "Please enter a valid phone number (7-15 digits)",
        }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setApiError("");

    try {
      const selected =
        countryCodes.find((c) => c.code === formData.countryCode) ||
        countryCodes[0];
      const result = await submitLead({
        name: formData.fullName,
        email: formData.email,
        countryDialCode: formData.countryCode.replace("+", ""),
        country: selected.label,
        phoneNumber: formData.phone,
        description: formData.message,
        packageTitle,
        packageAmount,
        tmSignUpType,
        geoData,
      });

      if (result.success) {
        window.location.href = `/thanks?customerid=${result.customerId}`;
      } else {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 md:p-6 w-full max-w-full overflow-hidden">
      <h3 className="font-heading text-[13px] sm:text-lg font-bold text-[#2d3132] mb-0.5 text-center leading-snug">
        {title}
      </h3>
      {subtitle && (
        <p className="text-gray-500 text-[11px] sm:text-xs mb-3 text-center">
          {subtitle}
        </p>
      )}
      {!subtitle && <div className="mb-3" />}
      {apiError && (
        <div
          className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md"
          data-testid="api-error"
        >
          <p className="text-xs text-red-700">{apiError}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-2.5">
        <div>
          <label
            htmlFor="fullName"
            className="block text-xs font-medium text-slate-700 mb-0.5"
          >
            Full Name *
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={`w-full px-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white ${errors.fullName ? "border-red-500" : "border-slate-300"}`}
            placeholder="John Doe"
            data-testid="input-fullname"
          />
          {errors.fullName && (
            <p className="mt-0.5 text-xs text-red-600">{errors.fullName}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-xs font-medium text-slate-700 mb-0.5"
          >
            Email Address *
          </label>
          <input
            type="text"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white ${errors.email ? "border-red-500" : "border-slate-300"}`}
            placeholder="john@example.com"
            data-testid="input-email"
          />
          {errors.email && (
            <p className="mt-0.5 text-xs text-red-600">{errors.email}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-xs font-medium text-slate-700 mb-0.5"
          >
            Phone Number *
          </label>
          <div className="flex gap-2">
            <CountryCodeSelect
              value={formData.countryCode}
              onChange={(code) =>
                handleChange({ target: { name: "countryCode", value: code } })
              }
              className="w-24 sm:w-28"
            />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={15}
              className={`flex-1 px-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white ${errors.phone ? "border-red-500" : "border-slate-300"}`}
              placeholder="1234567890"
              data-testid="input-phone"
            />
          </div>
          {errors.phone && (
            <p className="mt-0.5 text-xs text-red-600">{errors.phone}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="message"
            className="block text-xs font-medium text-slate-700 mb-0.5"
          >
            Description
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="2"
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white resize-none"
            placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
            data-testid="input-message"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="form-submit-btn"
          className="w-full bg-lime hover:bg-lime-dark text-white font-semibold py-2 px-4 rounded-md text-sm transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <span>Register Your Trademark</span>
              <Send className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
